import api from '@/services/api'
import { useAnalytics } from '@/composables/analytics'
import { usePlayer, wasDormant } from '@/composables/game/player'
import { computed, watch } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { showHiddenFeatures } from '@/composables/feature-flag'
import type { OnboardingStep } from '@/types/onboarding'
import Onboarding1 from '@/assets/images/onboarding/onboarding-1.webp'
import Onboarding2 from '@/assets/images/onboarding/onboarding-2.webp'
import Onboarding3 from '@/assets/images/onboarding/onboarding-3.webp'
import Onboarding4 from '@/assets/images/onboarding/onboarding-4.webp'
import Onboarding1Video from '@/assets/videos/onboarding/onboarding-1.mp4'
import Onboarding2Video from '@/assets/videos/onboarding/onboarding-2.mp4'
import Onboarding3Video from '@/assets/videos/onboarding/onboarding-3.mp4'
import Onboarding4Video from '@/assets/videos/onboarding/onboarding-4.mp4'

export const showOnboardedFromS1Modal = useLocalStorage(buildDropeeStorageKey('onboarded_from_s1'), false)

export const useOnboarding = () => {
  const { triggerEvent, setUserProperty } = useAnalytics()
  const { player, updatePlayer } = usePlayer()

  const isOnboardingDone = computed(() => player.value.onboarding.done)

  async function doOnboarding() {
    const { playerStats, isReferred } = await api.onboardingDone()
    updatePlayer(playerStats)
    triggerEvent({ event: 'tutorial_complete' })
    triggerEvent({ event: isReferred ? 'referral' : 'non_referral' })
    setUserProperty({ acquisition: isReferred ? 'referral' : 'direct' })
  }

  return {
    doOnboarding,
    isOnboardingDone,
  }

}

export const useOnboardingLayout = () => {

  const steps: OnboardingStep[] = [
    {
      index: 0,
      title: `A New <br/>Season <br/>Begins`,
      description: '',
      image: Onboarding1,
      video: Onboarding1Video,
    },
    {
      index: 1,
      title: `A Fresh Start. <br/>A New Empire.`,
      description: `<span class="text-shadow-1 text-teal-primary">Welcome to Dropee: Season 3!</span> <br/>
A new chapter begins — and the race to build the strongest empire starts now.
Everyone has the same goal: <span class="text-shadow-1 text-teal-primary">Rise from Broke Intern to Dropee Master.</span>`,
      image: Onboarding2,
      video: Onboarding2Video,
    },
    {
      index: 2,
      title: `Your Mission: <br/>Maximize your <br/>Profit per Hour`,
      description: `Upgrade cards, unlock new skills, and grow your company. Every decision boosts your <span class="text-shadow-1 text-teal-primary">Profit per Hour & unlock more rewards!</span>`,
      image: Onboarding3,
      video: Onboarding3Video,
    },
    {
      index: 3,
      title: `Play Daily. Climb Fast. Earn Big.`,
      description: `<p>Complete quests, win mini-games, and climb the leaderboards.</p>
<p class="mt-3">Season 3 brings new ways to earn and unlock exclusive rewards.</p>`,
      image: Onboarding4,
      buttonLabel: 'Let’s Go!',
      video: Onboarding4Video,
    },
  ]

  return {
    steps,
  }
}

const showOnboardingEmojiWheel = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_wheel'), true)
const showOnboardingEmojiNavbarHome = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_navbar_home'), false)
const showOnboardingEmojiTapSection = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_tap_section'), true)
const showOnboardingEmojiNavbarImprove = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_navbar_improve'), false)
const showOnboardingEmojiCard = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_card'), true)
const showOnboardingEmojiNavbarQuests = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_navbar_quests'), false)
const showOnboardingEmojiQuest = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_quest'), true)
const showOnboardingEmojiNavbarFriends = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_navbar_friends'), false)
const showOnboardingEmojiFriends = useLocalStorage(buildDropeeStorageKey('onboarding_emoji_friends'), true)

export const useOnboardingFingerEmojis = () => {
  const { player } = usePlayer()
  const canShowOnboardingEmoji = computed(() => !showHiddenFeatures.value && (player.value.level <= 1 || wasDormant.value))

  watch(showOnboardingEmojiWheel, (showOnboardingEmojiWheel, oldValue) => {
    if (!showOnboardingEmojiWheel && oldValue) {
      setTimeout(() => {
        showOnboardingEmojiNavbarHome.value = true
      }, 8000)
    }
  })

  watch(showOnboardingEmojiTapSection, (showOnboardingEmojiTapSection, oldValue) => {
    if (!showOnboardingEmojiTapSection && oldValue) {
      setTimeout(() => {
        showOnboardingEmojiNavbarImprove.value = true
      }, 500)
    }
  })

  watch(showOnboardingEmojiCard, (showOnboardingEmojiCard, oldValue) => {
    if (!showOnboardingEmojiCard && oldValue) {
      setTimeout(() => {
        showOnboardingEmojiNavbarQuests.value = true
      }, 1000)
    }
  })

  watch(showOnboardingEmojiQuest, (showOnboardingEmojiQuest, oldValue) => {
    if (!showOnboardingEmojiQuest && oldValue) {
      setTimeout(() => {
        showOnboardingEmojiNavbarFriends.value = true
      }, 2000)
    }
  })

  return {
    canShowOnboardingEmoji,
    showOnboardingEmojiWheel,
    showOnboardingEmojiNavbarHome,
    showOnboardingEmojiTapSection,
    showOnboardingEmojiNavbarImprove,
    showOnboardingEmojiCard,
    showOnboardingEmojiNavbarQuests,
    showOnboardingEmojiQuest,
    showOnboardingEmojiNavbarFriends,
    showOnboardingEmojiFriends,
  }
}
